import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Edit2, Eye, CheckCircle, AlertCircle, Copy, X
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserCoach } from '../../../lib/auth';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

interface LeadPage {
  id: string;
  slug: string;
  headline: string;
  description: string;
  cta_text: string;
  image_url: string | null;
  is_active: boolean;
}

export function LeadPageEditorTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [page, setPage] = useState<LeadPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const [form, setForm] = useState({
    slug: '',
    headline: 'YZ Kocumu Deneyin',
    description: 'YZ destekli kisisellestirilmis bir kocluk seansi deneyimleyin. Ihtiyaclariniza ozel aninda icgoruler ve rehberlik alin.',
    cta_text: 'Ucretsiz Basla',
    image_url: '',
    is_active: true,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) { navigate('/tr/login'); return; }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) { navigate('/tr/login'); return; }
        setCoachId(coach.id);

        const { data: existingPage } = await supabase
          .from('coach_lead_pages')
          .select('*')
          .eq('coach_id', coach.id)
          .maybeSingle();

        if (existingPage) {
          setPage(existingPage);
          setForm({
            slug: existingPage.slug,
            headline: existingPage.headline,
            description: existingPage.description,
            cta_text: existingPage.cta_text,
            image_url: existingPage.image_url || '',
            is_active: existingPage.is_active,
          });
        } else {
          const slug = user.email?.split('@')[0]?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'my-coach';
          setForm(f => ({ ...f, slug }));
        }
      } catch (err) {
        console.error(err);
        setError('Tanitim sayfasi ayarlari yuklenemedi');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId) return;
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const slug = form.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');

      if (page) {
        const { error: err } = await supabase
          .from('coach_lead_pages')
          .update({
            slug,
            headline: form.headline,
            description: form.description,
            cta_text: form.cta_text,
            image_url: form.image_url || null,
            is_active: form.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', page.id);

        if (err) throw err;
      } else {
        const { data: newPage, error: err } = await supabase
          .from('coach_lead_pages')
          .insert({
            coach_id: coachId,
            slug,
            headline: form.headline,
            description: form.description,
            cta_text: form.cta_text,
            image_url: form.image_url || null,
            is_active: form.is_active,
          })
          .select()
          .single();

        if (err) throw err;
        setPage(newPage);
      }

      setForm(f => ({ ...f, slug }));
      setSuccess('Tanitim sayfasi basariyla kaydedildi!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      if (err.code === '23505') {
        setError('Bu URL kisa adi zaten kullaniliyor. Lutfen farkli bir tane secin.');
      } else {
        setError(err.message || 'Tanitim sayfasi kaydedilemedi');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageUrl = `${window.location.origin}/try/${form.slug}`;

  const copyUrl = async () => {
    await navigator.clipboard.writeText(pageUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Tanitim sayfasi ayarlari yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-teal-500 rounded-xl shadow-soft">
                <Edit2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">YZ Koc Tanitim Sayfasi</h1>
                <p className="freud-text-sm text-chocolate-500">
                  Herkese acik tanitim sayfanizi ozellestirin
                </p>
              </div>
            </div>
            {page && (
              <a
                href={pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="freud-btn-secondary flex items-center gap-2 text-sm"
              >
                <Eye className="w-4 h-4" />
                Onizleme
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="bg-sage-100 border border-sage-200 text-sage-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="freud-card-elevated p-6">
            <h2 className="text-lg font-bold text-chocolate-900 mb-6">Sayfa Ayarlari</h2>
            <form onSubmit={handleSave} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">URL Kisa Adi</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-chocolate-400">/try/</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    required
                    className="freud-input flex-1"
                    placeholder="koclugum"
                  />
                </div>
                {page && (
                  <button type="button" onClick={copyUrl} className="mt-2 flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-800">
                    {copiedUrl ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedUrl ? 'Kopyalandi!' : 'Sayfa URL kopyala'}
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Baslik</label>
                <input
                  type="text"
                  value={form.headline}
                  onChange={(e) => setForm({ ...form, headline: e.target.value })}
                  required
                  className="freud-input"
                  placeholder="YZ Kocumu Deneyin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Aciklama</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                  rows={4}
                  className="freud-input resize-none"
                  placeholder="Ziyaretcilere ne deneyimleyeceklerini anlatin..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Buton Metni</label>
                <input
                  type="text"
                  value={form.cta_text}
                  onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                  required
                  className="freud-input"
                  placeholder="Ucretsiz Basla"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Gorsel URL (istege bagli)</label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="freud-input"
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-cream-100 rounded-xl">
                <span className="text-sm font-medium text-chocolate-700">Sayfa Aktif</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_active: !form.is_active })}
                  className="flex-shrink-0"
                >
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.is_active ? 'bg-sage-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.is_active ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  </div>
                </button>
              </div>

              <button type="submit" className="freud-btn-primary w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Kaydediliyor...' : 'Sayfayi Kaydet'}
              </button>
            </form>
          </div>

          <div>
            <h2 className="text-lg font-bold text-chocolate-900 mb-4">Canli Onizleme</h2>
            <div className="border border-cream-300 rounded-xl overflow-hidden shadow-lg">
              <div className="bg-gradient-to-br from-teal-600 to-teal-800 p-8 text-center">
                {form.image_url && (
                  <img src={form.image_url} alt="" className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-4 border-white/20" />
                )}
                <h3 className="text-2xl font-bold text-white mb-3">{form.headline || 'Basliginiz'}</h3>
                <p className="text-teal-100 text-sm leading-relaxed max-w-sm mx-auto">
                  {form.description || 'Aciklamaniz burada gorunecek...'}
                </p>
              </div>
              <div className="bg-white p-6">
                <div className="space-y-3 max-w-xs mx-auto">
                  <div className="h-10 bg-cream-100 rounded-lg border border-cream-300 flex items-center px-3">
                    <span className="text-xs text-chocolate-400">Ad Soyad</span>
                  </div>
                  <div className="h-10 bg-cream-100 rounded-lg border border-cream-300 flex items-center px-3">
                    <span className="text-xs text-chocolate-400">E-posta Adresi</span>
                  </div>
                  <button className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold">
                    {form.cta_text || 'Basla'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
